<template>
  <modal
    class="uk-modal-container"
    ref="modal"
    :title="title"
    :auto-overflow="true"
    @show="onShow"
    @hide="onHide"
    @hidden="onHidden"
  >
    <file-upload-button
      :attachment-type="attachmentType"
      :multiple="multiple"
      @change="refetchList"
    />
    <search-input ref="search" v-model="searchQuery" class="uk-align-right" />

    <data-grid
      v-if="gridLoaded"
      ref="dataGrid"
      class="uk-margin-medium"
      table-class="uk-table-middle"
      :columns="gridColumns"
      :sortable="['name', 'updatedAt']"
      :selectable="true"
      :multiple="multiple"
      :rows="attachments.entries"
      :sort-info="attachments.sort"
      :page-info="attachments.pagination"
      :is-loading="isLoading"
      :show-actions="false"
      :change-route="false"
      @change-page="onChangePage"
      @change-selection="onChangeSelection"
      @sort="sortBy"
    >
      <span slot="empty">{{$t('hints.empty')}}</span>
      <template slot="beforeHead">
        <th></th>
      </template>
      <template slot="beforeRow" scope="props">
        <td class="attachment-picker__thumbnail-cell">
          <thumbnail
            :attachment="props.row"
            class="attachment-picker__thumbnail"
          />
        </td>
      </template>
      <template slot="name" scope="props">
        <attachment-name :attachment="props.row" />
      </template>
      <template slot="type" scope="props">
        {{formatTypeName(props.row.type)}}
      </template>
      <template slot="updatedAt" scope="props">
        <timeago :since="props.row.updatedAt" :auto-update="60" />
      </template>
    </data-grid>

    <div
      v-else
      class="loader-container uk-container uk-margin-medium-top uk-text-center"
    >
      <loader />
    </div>

    <common-button
      slot="buttons"
      :text="$t('actions.select')"
      type="primary"
      @click="submit"
      :disabled="!hasSelection"
    />
  </modal>
</template>

<script>
import camelCase from 'lodash/camelCase';
import AttachmentFilterForm from '@/components/AttachmentFilterForm';
import AttachmentName from '@/components/AttachmentName';
import attachmentsQuery from '@/api/queries/attachments.graphql';
import CommonButton from '@/components/CommonButton';
import DropdownButton from '@/components/DropdownButton';
import FileUploadButton from '@/components/FileUploadButton';
import IndexView from '@/mixins/IndexView';
import Modal from '@/components/Modal';
import Thumbnail from '@/components/Thumbnail';

export default {
  name: 'attachment-picker',
  mixins: [
    IndexView('attachments', {
      gridFields: ['name', 'type', 'updatedAt'],
      listQuery: attachmentsQuery,
      useInternalFilter: true,
      defaultFilter() {
        return {
          type: this.attachmentType,
          omittedIds: this.selectedAttachmentIds,
        };
      },
    }),
  ],
  components: {
    AttachmentFilterForm,
    AttachmentName,
    CommonButton,
    DropdownButton,
    FileUploadButton,
    Modal,
    Thumbnail,
  },
  data() {
    return {
      selectedAttachments: [],
      isShown: false,
    };
  },
  props: {
    attachmentType: String,
    multiple: {
      type: Boolean,
      default: true,
    },
    selectedAttachmentIds: {
      type: Array,
      default: () => [],
    },
    showType: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    queryVariables() {
      return {
        filter: this.filterInfoWithDefault,
        pagination: this.pageInfo,
        sort: this.sortInfo,
        search: this.searchQuery,
      };
    },
    title() {
      const amount = this.multiple ? 'multiple' : 'single';
      if (!this.attachmentType) return this.$t(`titles.${amount}.generic`);
      return this.$t(`titles.${amount}.${camelCase(this.attachmentType)}`);
    },
    hasSelection() {
      return this.selectedAttachments.length > 0;
    },
    skipQuery() {
      return !this.isShown;
    },
  },
  methods: {
    show() {
      this.$refs.modal.show();
    },
    hide() {
      this.$refs.modal.hide();
    },
    submit() {
      this.hide();
      this.$emit('select', this.selectedAttachments);
    },
    onChangePage(pagination) {
      this.internalPagination = pagination;
    },
    onChangeSelection({ selectedRows }) {
      this.selectedAttachments = selectedRows;
    },
    onShow() {
      this.isShown = true;
    },
    onHide() {
      this.isShown = false;
    },
    onHidden() {
      this.selectedAttachments = [];
      this.$refs.dataGrid.unselectAll();
    },
    formatTypeName(type) {
      return this.$t(`attachmentTypes.${camelCase(type)}`);
    },
  },
  i18n: {
    messages: {
      de: {
        actions: {
          select: 'Auswählen',
        },
        hints: {
          empty: 'Keine Dateianhänge gefunden.',
        },
        titles: {
          single: {
            document: 'Dokument auswählen',
            generic: 'Datei auswählen',
            image: 'Bild auswählen',
            mediaObject: 'Mediendatei auswählen',
            video: 'Video auswählen',
          },
          multiple: {
            document: 'Dokumente auswählen',
            generic: 'Dateien auswählen',
            image: 'Bilder auswählen',
            mediaObject: 'Mediendateien auswählen',
            video: 'Videos auswählen',
          },
        },
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.attachment-picker {
  &__thumbnail-cell {
    width: 100px;
  }

  &__thumbnail {
    width: 100%;
  }
}
</style>



// WEBPACK FOOTER //
// AttachmentPicker.vue?7a736052